import { useEffect, useRef, useState } from "react";
import "../../../../../css/AML/Checks/Check.css";
import SecondaryButton from "../../Components/Buttons/SecondaryButton";
import Banner from "../../Components/Banners/Banner";
import { ReactComponent as PersonalInformationIcon } from "../../../../../assets/icons/aml/personalInformation.svg";
import { ReactComponent as AddressIcon } from "../../../../../assets/icons/aml/currentAddress.svg";
import { ReactComponent as ContactIcon } from "../../../../../assets/icons/aml/contactInfo.svg";
import TextInput from "../../Components/Inputs/TextInput";
import DateInput from "../../Components/Inputs/DateInput";
import MobileInput from "../../Components/Inputs/MobileNumberInput";
import SearchInput from "../../Components/Inputs/SearchInput";
import "../../../../../css/AML/Buttons/RadioButton.css";
import { isValidForm, buildPayload } from "../utils/amlForm";
import {
  makePhoneChange,
  makeHandleChange,
  makeDobChange,
} from "../utils/factories/factories";
import {
  initialEnhancedState,
  validateEnhanced,
} from "../utils/checkStates/enhancedState";
import SubmissionPopUp from "../../Components/PopUp/SubmissionPopUp";
import { auth, dbAML } from "../../../../../backend/firebaseconfig";
import { useUserEmail } from "../../../../../context/UserEmailContext";
import { addDoc, collection, updateDoc, setDoc, doc } from "firebase/firestore";
import { getEndUserId } from "../utils/helperFunctions/getEndUserId";
import { useAdminAdviserMails } from "../../../../../context/AdminAdviserEmailsContext";
import { useNavigate } from "react-router-dom";
import {
  getAddressAutocomplete,
  getAddressById,
} from "../utils/postcodes/getAddressClient";

const EnhancedCheck = ({
  selectedForAdviserEmail = "",
  selectedForAdviserBrandName = "",
}) => {
  const [formData, setFormData] = useState(initialEnhancedState);
  const [errors, setErrors] = useState({});
  const AML_SERVER = process.env.REACT_APP_AML_SERVER;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const handleChange = makeHandleChange(setFormData, setErrors);
  // const handleIdDocChange = makeIdDocChange(setFormData, setErrors);
  const handleDobChange = makeDobChange(setFormData, setErrors);
  const handlePhoneChange = makePhoneChange(setFormData, setErrors);

  const [showSubmissionPopUp, setShowSubmissionPopUp] = useState(false);
  const { userEmail } = useUserEmail();
  const { currentAdviserEmail } = useAdminAdviserMails();

  // ---------------- Address search (getAddress.io) ----------------
  const [postcodeSearch, setPostcodeSearch] = useState(
    initialEnhancedState?.address?.postcode || "",
  );

  // separate variable used for API calls (as you requested)
  const [postcodeSearchTerm, setPostcodeSearchTerm] = useState("");

  const [addrState, setAddrState] = useState({ state: "idle", msg: "" });
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [showAddrDropdown, setShowAddrDropdown] = useState(false);

  const addrDebounceRef = useRef(null);
  const autoAbortRef = useRef(null);
  const getAbortRef = useRef(null);

  const cancel = (ref) => {
    try {
      ref.current?.abort?.();
    } catch {}
  };

  // keep search input synced if postcode is updated elsewhere (e.g. selecting address)
  useEffect(() => {
    const pc = formData.address?.postcode || "";
    setPostcodeSearch(pc);
    setPostcodeSearchTerm(pc);
  }, [formData.address?.postcode]);

  const runAutocomplete = async (term) => {
    const q = String(term || "").trim();

    if (q.length < 2) {
      setAddrSuggestions([]);
      setShowAddrDropdown(false);
      setAddrState({ state: "idle", msg: "" });
      return;
    }

    cancel(autoAbortRef);
    const ac = new AbortController();
    autoAbortRef.current = ac;

    setAddrState({ state: "loading", msg: "Searching addresses..." });

    const out = await getAddressAutocomplete(q, { signal: ac.signal });

    if (out?.aborted || ac.signal.aborted) return;
    if (!out.ok) {
      setAddrSuggestions([]);
      setShowAddrDropdown(false);
      setAddrState({ state: "error", msg: out.error || "Autocomplete failed" });
      return;
    }

    const list = out.suggestions || [];
    setAddrSuggestions(list);
    setShowAddrDropdown(list.length > 0);
    setAddrState({
      state: "success",
      msg: list.length ? `Found ${list.length} results` : "No results",
    });
  };

  const applyAddressFromId = async (id) => {
    cancel(getAbortRef);
    const ac = new AbortController();
    getAbortRef.current = ac;

    setAddrState({ state: "loading", msg: "Fetching address..." });

    const out = await getAddressById(id, { signal: ac.signal });

    if (!out.ok) {
      setAddrState({
        state: "error",
        msg: out.error || "Failed to fetch address",
      });
      return;
    }

    const a = out.address || {};

    // map to your form fields
    // handleChange("address", "buildingNumber", "text")(a.building_number || "");
    handleChange(
      "address",
      "buildingNumber",
      "text",
    )(String(a.building_number || "").slice(0, 10));
    handleChange("address", "buildingName", "text")(a.building_name || "");
    handleChange("address", "street", "text")(a.thoroughfare || a.line_1 || "");
    handleChange("address", "town", "text")(a.town_or_city || "");
    handleChange("address", "county", "text")(a.county || "");

    const nextPc =
      a.postcode || formData.address?.postcode || postcodeSearch || "";
    handleChange("address", "postcode", "text")(nextPc);

    setPostcodeSearch(nextPc);
    setPostcodeSearchTerm(nextPc);

    setShowAddrDropdown(false);
    setAddrSuggestions([]);
    setAddrState({ state: "success", msg: "Address selected" });
  };

  useEffect(() => {
    return () => {
      cancel(autoAbortRef);
      cancel(getAbortRef);
      if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
    };
  }, []);

  const resetAll = () => {
    setFormData(initialEnhancedState);
    setErrors({});
    // address search UI
    setPostcodeSearch("");
    setPostcodeSearchTerm("");
    setAddrSuggestions([]);
    setShowAddrDropdown(false);
    setAddrState({ state: "idle", msg: "" });
  };

  // =====================================================
  // FRONTEND — Enhanced handleSubmit (same as Standard pattern)
  // =====================================================
  const handleSubmit = async () => {
    const nextErrors = validateEnhanced(formData);
    setErrors(nextErrors);

    if (!isValidForm(nextErrors)) return;
    if (isSubmitting) return;

    const endUserId = getEndUserId(auth.currentUser?.uid);
    const base = (AML_SERVER || "").replace(/\/$/, "");
    if (!base) {
      setErrors((p) => ({ ...p, _server: "AML server URL missing" }));
      return;
    }

    const experianData = {
      person: {
        firstName: formData.personal.firstName,
        lastName: formData.personal.lastName,
        dob: formData.personal.dob,
      },
      address: {
        buildingNumber: formData.address.buildingNumber || "",
        buildingName: formData.address.buildingName || "",
        street: formData.address.street,
        town: formData.address.town,
        county: formData.address.county,
        postcode: formData.address.postcode,
      },
      documents: formData?.documents || [],
    };

    const effectiveForAdviserEmail =
      selectedForAdviserEmail || currentAdviserEmail || "";

    const ownerEmail = effectiveForAdviserEmail || userEmail || "";
    const adviserBrandName = String(selectedForAdviserBrandName || "").trim();

    const payload = {
      ...buildPayload(formData, "Enhanced"),
      ownerEmail,
      forAdviser: effectiveForAdviserEmail || null,
      adviserBrandName,
      experianData,
    };

    let docRef;
    let vendorData;

    try {
      setIsSubmitting(true);

      docRef = await addDoc(
        collection(dbAML, "AML Checks", userEmail, "Checks"),
        {
          checkType: "Enhanced",
          createdAt: new Date().toISOString(),
          createdBy: userEmail || null,
          forAdviser: effectiveForAdviserEmail || null,
          adviserBrandName,
        },
      );

      vendorData = docRef.id;

      setShowSubmissionPopUp(true);

      await updateDoc(docRef, {
        vendorData,
        endUserId: endUserId || "",
        experianStatus: "processing",
        updatedAt: new Date().toISOString(),
      });

      await addDoc(collection(docRef, "Request Data"), {
        ...payload,
        endUserId: endUserId || "",
        createdAt: new Date().toISOString(),
      });

      const vRes = await fetch(`${base}/enhanced-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          data: payload,
          vendorData,
          ownerEmail,
          adviserBrandName,
          endUserId: endUserId || "",
        }),
      });

      const vJson = await vRes.json().catch(() => ({}));

      if (!vRes.ok) {
        await updateDoc(docRef, {
          status: "failed",
          experianStatus: "failed",
          errorMessage:
            vJson?.error || vJson?.message || "Failed to start Enhanced check",
          updatedAt: new Date().toISOString(),
        });
        throw new Error(
          vJson?.error || vJson?.message || "Failed to start Enhanced check",
        );
      }

      const sessionId = vJson?.sessionId || null;
      const sessionUrl = vJson?.sessionUrl || null;

      if (sessionId) {
        await updateDoc(docRef, {
          sessionId,
          sessionUrl,
          updatedAt: new Date().toISOString(),
        });
      }

      const experianPromise = (async () => {
        const eRes = await fetch(`${base}/enhanced-check/experian`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            data: payload,
            vendorData,
            endUserId: endUserId || "",
          }),
        });

        if (eRes.status !== 200) {
          throw new Error(
            "Experian returned a non-200 response. The team has been notified.",
          );
        }

        const eJson = await eRes.json();

        const expRequestId =
          eJson?.expRequestId ||
          eJson?.responseHeader?.expRequestId ||
          eJson?.experian?.expRequestId ||
          null;

        const expDocRef = doc(docRef, "Experian Response", "Response");

        await setDoc(
          expDocRef,
          {
            experianStatus: eJson?.experianStatus || "completed",
            expRequestId,
            response: eJson,
            updatedAt: new Date().toISOString(),
            updatedBy: "frontend",
          },
          { merge: true },
        );

        await updateDoc(docRef, {
          experianStatus: eJson?.experianStatus || "completed",
          expRequestId,
          updatedAt: new Date().toISOString(),
        });

        return eJson;
      })();

      experianPromise.catch(async (e) => {
        try {
          await updateDoc(docRef, {
            experianStatus: "failed",
            errorMessage: e?.message || "Experian failed",
            updatedAt: new Date().toISOString(),
          });
        } catch {}
      });

      // resetAll();
    } catch (err) {
      if (docRef) {
        try {
          await updateDoc(docRef, {
            status: "failed",
            experianStatus: "failed",
            errorMessage: err?.message || "Failed to start enhanced check",
            updatedAt: new Date().toISOString(),
          });
        } catch {}
      }
      // resetAll();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="Check-Container">
      {/* <Banner
        type="info"
        message="Required when client has not been seen face to face or via video call."
      /> */}

      <div className="Check-Content">
        {/* Personal Information */}
        <div className="Check-Section">
          <div className="Check-Section-Header">
            <PersonalInformationIcon />
            <div className="AML-Text-Input">Personal Information</div>
          </div>

          <div className="Check-Section-Inputs">
            <TextInput
              label="First Name"
              value={formData.personal.firstName}
              isRequired
              onChange={handleChange("personal", "firstName", "text")}
              placeholder="Alex"
              error={errors?.personal?.firstName}
            />
            <TextInput
              label="Last Name"
              value={formData.personal.lastName}
              isRequired
              onChange={handleChange("personal", "lastName", "text")}
              placeholder="Smith"
              error={errors?.personal?.lastName}
            />
            <DateInput
              label="Date of Birth"
              value={formData.personal.dob}
              isRequired
              onChange={handleDobChange}
              placeholderText="DD/MM/YYYY"
              dateFormat="DD/MM/YYYY"
              isError={errors?.personal?.dob}
            />
          </div>
        </div>

        <div className="Horizontal-Line" />

        {/* Contact Information */}
        <div className="Check-Section">
          <div className="Check-Section-Header">
            <ContactIcon />
            <div className="AML-Text-Input">Contact Information</div>
          </div>

          <div className="Check-Section-Inputs">
            <TextInput
              label="Email Address"
              value={formData.personal.email}
              isRequired
              onChange={handleChange("personal", "email", "email")}
              placeholder="example@example.com"
              error={errors?.personal?.email}
            />
            {/* <MobileInput
              label="Phone Number"
              value={formData.personal.phone}
              isRequired
              onChange={handlePhoneChange}
              placeholder="7123 456789"
              error={errors?.personal?.phone}
            /> */}
          </div>
        </div>

        <div className="Horizontal-Line" />

        {/* Current Address */}
        <div className="Check-Section">
          <div className="Check-Section-Header">
            <AddressIcon />
            <div className="AML-Text-Input">Current Address</div>
          </div>
          <Banner
            type="info"
            message="Please enter a valid UK address, including the building number or name."
          />
          {/* postcode/address search (SearchInput + lookup + dropdown) */}
          <div className="aml-postcode-wrapper">
            <SearchInput
              label="Address Lookup"
              placeholder="Start typing the address or postcode..."
              value={postcodeSearch}
              onChange={(val) => {
                setPostcodeSearch(val);

                // keep formData in sync (NO normalisation)
                handleChange("address", "postcode", "text")(val);

                // separate API term
                setPostcodeSearchTerm(val);

                if (addrDebounceRef.current)
                  clearTimeout(addrDebounceRef.current);
                addrDebounceRef.current = setTimeout(
                  () => runAutocomplete(val),
                  300,
                );
              }}
              onSubmit={() => runAutocomplete(postcodeSearchTerm)}
            />

            {/* <div className="aml-postcode-actions">
              <SecondaryButton
                label={
                  addrState.state === "loading" ? "Searching..." : "Lookup"
                }
                onClick={() => runAutocomplete(postcodeSearchTerm)}
                isDisabled={addrState.state === "loading"}
              />
            </div> */}

            {showAddrDropdown && addrSuggestions.length > 0 && (
              <div className="aml-postcode-dropdown">
                {addrSuggestions.map((sug) => (
                  <div
                    key={sug.id}
                    className="aml-postcode-option AML-Text-Body"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      applyAddressFromId(sug.id);
                    }}
                  >
                    {sug.address}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Address fields */}
          <div className="Check-Section-Inputs">
            <TextInput
              label="Building Number"
              value={formData.address.buildingNumber}
              onChange={handleChange("address", "buildingNumber", "text")}
              placeholder="123"
              isRequired={false}
              error={errors?.address?.buildingNumber}
              maxLength={10}
            />
            <TextInput
              label="Building Name"
              value={formData.address.buildingName}
              onChange={handleChange("address", "buildingName", "text")}
              placeholder="High Tower"
              isRequired={false}
              error={errors?.address?.buildingName}
            />
            <TextInput
              label="Street"
              value={formData.address.street}
              isRequired
              onChange={handleChange("address", "street", "text")}
              placeholder="High Street"
              error={errors?.address?.street}
            />
            <TextInput
              label="Town"
              value={formData.address.town}
              isRequired
              onChange={handleChange("address", "town", "text")}
              placeholder="London"
              error={errors?.address?.town}
            />
            <TextInput
              label="County"
              value={formData.address.county}
              isRequired
              onChange={handleChange("address", "county", "text")}
              placeholder="Greater London"
              error={errors?.address?.county}
            />
            <TextInput
              label="Postcode"
              value={formData.address.postcode}
              isRequired
              onChange={(e) => {
                // keep as-is (NO normalisation)
                const typed = e.target.value;
                handleChange("address", "postcode", "text")(typed);
                setPostcodeSearch(typed);
                setPostcodeSearchTerm(typed);
              }}
              placeholder="AB1 2CD"
              error={errors?.address?.postcode}
            />
          </div>
        </div>
      </div>

      <SecondaryButton
        label={isSubmitting ? "Submitting..." : "Submit"}
        onClick={handleSubmit}
        isDisabled={false}
      />

      {showSubmissionPopUp && (
        <SubmissionPopUp
          onClose={() => {
            setShowSubmissionPopUp(false);
            navigate("/check²");
          }}
        />
      )}
    </div>
  );
};

export default EnhancedCheck;
